import { AsyncPipe, DOCUMENT, KeyValuePipe } from '@angular/common';
import { Component, inject, Signal, signal, WritableSignal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, NavigationSkipped, Router, RouterLink } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import posthog from 'posthog-js';
import { BehaviorSubject } from 'rxjs';
import { DownloadService, StorageService } from '@core/services';
import { SessionAuth } from '@legacy-models/auth/user';
import { DownloadRequest } from '@legacy-models/portal/Download';

@Component({
  selector: 'oke-header',
  imports: [RouterLink, ReactiveFormsModule, AsyncPipe, SvgIconComponent, KeyValuePipe],
  templateUrl: './header.html',
  styleUrl: './header.scss'
})
export class Header {
  private readonly _downloadRequestsSource = new BehaviorSubject<DownloadRequest[]>([]);
  protected readonly downloadRequests$ = this._downloadRequestsSource.asObservable();
  private readonly _$isNotInvoiceSummaryView: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly $isNotInvoiceSummaryView: Signal<boolean> = this._$isNotInvoiceSummaryView.asReadonly();
  protected readonly $isDownloadVisible: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly $numActiveDownloads: WritableSignal<number> = signal<number>(0);
  protected readonly $currentRoute: WritableSignal<string> = signal<string>('');
  user: SessionAuth | null = null;
  isDashboard = false;
  appTitleSource = new BehaviorSubject<string | null>(null);
  appTitle$ = this.appTitleSource.asObservable();
  private readonly _downloadSvc: DownloadService = inject(DownloadService);
  readonly storageSvc: StorageService = inject(StorageService);
  private readonly _doc: Document = inject(DOCUMENT);
  private readonly _router: Router = inject(Router);

  constructor() {
    this._downloadSvc.requests$.subscribe(this._onDownloadRequestsChanged.bind(this));
    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.$currentRoute.set(event.url);
        this._$isNotInvoiceSummaryView.set(true);
      } else if (event instanceof NavigationSkipped) {
        // THIS 👉🏻 router.getCurrentNavigation()?.extras.info 👈🏻
        //   - can be anything
        this._$isNotInvoiceSummaryView.set(!!this._doc.querySelector('nggp-invoices invoice-summary') === false);
      }
    });
    this.storageSvc.currentUser$.subscribe(currUser => {
      if (currUser) {
        this.user = currUser;
      }
    });
    this.storageSvc.currentPage$.subscribe(this._onCurrentPageChanged.bind(this));
  }

  /**
   * Method is fired any time the Download Service `requests$`
   *   Observable is changed
   * @param downloadRequests `DownloadRequest[]`
   */
  private _onDownloadRequestsChanged(downloadRequests: DownloadRequest[]): void {
    this._downloadRequestsSource.next(downloadRequests);
    this.$isDownloadVisible.set(
      this._downloadRequestsSource.getValue().some(request => request.isInProgress === false && !request.userWasPresent)
    );
    this.$numActiveDownloads.set(
      this._downloadRequestsSource.getValue().filter(request => request.isInProgress === true).length
    );
  }

  protected removeDownloads(): void {
    const ACKNOWLEDGED_DOWNLOAD_IDS = this._downloadRequestsSource
      .getValue()
      .filter(request => request.isInProgress === false && !request.userWasPresent)
      .map<string>(request => request.id);
    this._downloadSvc.clearRequests(ACKNOWLEDGED_DOWNLOAD_IDS);
  }

  private _onCurrentPageChanged(val: string | null): void {
    if (!val) {
      return;
    }
    if (['gathering & processing', 'intrastate pipelines'].includes(val.toLowerCase())) {
      this.isDashboard = true;
    } else {
      this.isDashboard = false;
    }
    val = val === 'NGGP Outages' ? 'NGGP Planned Outages' : val;
    this.appTitleSource.next(val);
  }

  toggleSidebarMenu(): void {
    this._doc.body.classList.toggle('sidebar--engaged');
    if (this._doc.defaultView!.innerWidth >= 768) {
      posthog.capture('app:sidebar_visibility_button_clicked', {
        is_hidden: this._doc.body.classList.contains('sidebar--engaged')
      });
    }
  }

  // toggleSearchFormVisibility() {
  //   this._doc.body.classList.toggle('is-searching');
  //   setTimeout(() => {
  //     this._doc.getElementById('app-header-search-input').focus();
  //   }, 100);
  // }

  get routeHome(): string {
    return sessionStorage.getItem('routeHome') as string;
  }

  isExternalPageView(): boolean {
    const externalPageRoutes = [
      '/unsubscribe',
      '/onboard',
      '/login',
      '/register',
      '/mfa',
      '/forgotPassword',
      '/resetPassword'
    ];

    return (
      externalPageRoutes.includes(this.$currentRoute()) ||
      this.$currentRoute().includes('register') ||
      this.$currentRoute().includes('session') ||
      this.$currentRoute().includes('unsubscribe')
    );
  }
}
